const { sha256 } = require('@noble/hashes/sha256');
const { bytesToHex } = require('@noble/hashes/utils');

// Versioned SHA-256 of full canonical evidence. Never match by rule alone.
// Presentation text is excluded. Structured rules compare source values,
// so a language switch or a different input row order does not reopen the same facts.
function canonical(value) {
    if (Array.isArray(value)) return value.map(canonical).sort((a, b) => {
        const left = JSON.stringify(a), right = JSON.stringify(b);
        return left < right ? -1 : left > right ? 1 : 0;
    });
    // JSON persistence omits undefined object fields, but retains null (including undefined array entries).
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).filter(k => value[k] !== undefined).sort().map(k => [k, canonical(value[k])]));
    return value === undefined ? null : value;
}
function structuredEvidence(issue) {
    const values = row => row && typeof row === 'object'
        ? Object.fromEntries(Object.entries(row).filter(([k]) => !['name', 'accName', 'missingInvoices'].includes(k)))
        : row;
    if (!issue.evidence) return (issue.details || []).map(values);
    return Array.isArray(issue.evidence) ? issue.evidence.map(values)
        : [issue.evidence, (issue.details || []).map(values)];
}
function findingEvidenceKey(issue, period) {
    if (!issue?.ruleId || (!issue.evidence && !issue.details?.length)) return null;
    const key = JSON.stringify([2, period?.year || null, period?.monthFrom || 1, period?.monthTo || null, issue.ruleId, issue.type, issue.specialType || null,
        canonical(structuredEvidence(issue))]);
    return 'v2:' + bytesToHex(sha256(key));
}

function matchingAck(acks, issue, period) {
    const key = findingEvidenceKey(issue, period);
    if (!key || !period?.year || !period?.monthTo) return null;
    return (acks || []).filter(a => a.rule_id === issue.ruleId && a.evidence_key === key
        && Number(a.evidence_year) === Number(period.year)
        && Number(a.evidence_month) === Number(period.monthTo)
        && (!a.valid_until || Number(a.valid_until) >= Date.now() / 1000))
        .sort((a, b) => (b.revision || 0) - (a.revision || 0) || (b.created_at || 0) - (a.created_at || 0) || (b.id || 0) - (a.id || 0))[0] || null;
}

const isResolvedAck = ack => !!ack && ['checked', 'fixed'].includes(ack.action);

const ackEvidence = (issue, period) => ({
    evidence_key: findingEvidenceKey(issue, period),
    evidence_year: period?.year,
    evidence_month: period?.monthTo,
});

// Old amortization reports stored the asset snapshot on a separate information
// card. Restore a bounded identity for that saved warning, never a rule-only key.
// Pure read adapter: no historical report/ack mutation and no automatic approval.
function withSavedFindingEvidence(issues) {
    if (!Array.isArray(issues)) return issues;
    const targets = issues.filter(i => i?.ruleId === 'amortization_12xx' && ['warning', 'error'].includes(i.type));
    if (targets.length !== 1) return issues;
    const target = targets[0];
    if (target.specialType || target.evidence || !Array.isArray(target.details) || target.details.length) return issues;
    const snapshots = issues.filter(i => i?.ruleId === 'amortization_12xx'
        && i.type === 'info' && i.specialType === 'amortization');
    if (snapshots.length !== 1 || !Array.isArray(snapshots[0].details) || !snapshots[0].details.length) return issues;
    const assets = snapshots[0].details;
    const valid = row => row && Number.isInteger(row.accNum) && row.accNum >= 1200 && row.accNum <= 1299
        && Number.isFinite(row.ub) && ['ib', 'change'].every(k => row[k] === undefined || Number.isFinite(row[k]))
        && (row._subAccounts === undefined || (Array.isArray(row._subAccounts) && row._subAccounts.length > 0 && row._subAccounts.every(valid)));
    if (!assets.every(valid)) return issues;
    const values = row => Object.fromEntries(Object.entries(row).filter(([key]) => !['name', 'accName'].includes(key))
        .map(([key, value]) => [key, key === '_subAccounts' ? value.map(values) : value]));
    return issues.map(issue => issue?.ruleId === 'amortization_12xx'
        && ['warning', 'error'].includes(issue.type) && !issue.specialType
        && !issue.evidence && Array.isArray(issue.details) && issue.details.length === 0
        ? { ...issue, evidence: { source: 'saved-amortization-assets-v1', assets: assets.map(values) } }
        : issue);
}

function isRetiredFinding(issue) {
 // Legacy cards carry the checked Brreg result in verified lines, not structured evidence.
 const registeredAuditor = issue?.ruleId === 'revisjonsplikt' && Array.isArray(issue.verified)
  && issue.verified.some(line => /^(?:Rewident zarejestrowany w Brreg:|Registrert revisor i Brreg:) .+\(\d{9}\)\. (?:Odczyt:|Lest:) \d{4}-\d{2}-\d{2}/.test(line))
  && issue.verified.some(line => /https:\/\/data\.brreg\.no\/enhetsregisteret\/api\/enheter\/\d{9}\/roller(?:\s|$)/.test(line));
 return registeredAuditor || issue?.ruleId === 'bank_saldo_eom' ||
  (['invoice_numbering','mva_per_voucher'].includes(issue?.ruleId) && issue.specialType !== 'rule_not_checked');
}

module.exports = { findingEvidenceKey, matchingAck, isResolvedAck, ackEvidence, isRetiredFinding, withSavedFindingEvidence };
